import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import { TranslatorService } from '@sc/data-access-translation';
import { Observable } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '../icon/icon.component';
import { NgFor, NgIf, NgClass, AsyncPipe } from '@angular/common';

@Component({
  selector: 'sc-stepper',
  styleUrls: ['./stepper.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav
      class="stepper-wrapper"
      role="navigation"
      [attr.aria-label]="
        'GlobalApplicativeConstants.accessibility.labels.declarationSteps'
          | translate: { currentStep: (getCurrentStepTitle() | translate) }
      "
    >
      <div class="fixed-breadcrumbs-container sc-design-caption">
        <ng-container
          *ngFor="
            let breadcrumbItem of fixedBreadcrumbItems | async;
            let isFirst = first
          "
        >
          <sc-icon
            *ngIf="!isFirst"
            class="fixed-breadcrumbs-item-separator"
            name="arrow_right"
          ></sc-icon>
          <p class="fixed-breadcrumbs-item" [innerHTML]="breadcrumbItem"></p>
        </ng-container>
      </div>
      <div class="stepper-list-wrapper">
        <ol class="stepper-list">
          <li
            *ngFor="let step of steps; let counter = index"
            class="stepper-item"
            [ngClass]="{
              'stepper-item-complete': isStepComplete(counter),
              'stepper-item-active': isStepActive(counter),
              'stepper-item-disable': isStepDisabled(counter)
            }"
            [attr.aria-current]="getAriaCurrent(counter)"
          >
            <div
              class="sc-design-text-3-bold stepper-item-counter"
              [id]="'stepperButton' + (counter + 1)"
              [attr.tabIndex]="isStepDisabled(counter) ? undefined : '0'"
              [attr.role]="isStepDisabled(counter) ? undefined : 'button'"
              (click)="callStepAction($event, step, counter)"
              (keydown.enter)="callStepAction($event, step, counter)"
              (keydown.space)="callStepAction($event, step, counter)"
            >
              <span>{{ counter + 1 }}</span>
              <sc-icon name="check_bis"></sc-icon>
            </div>
            <div
              class="sc-design-caption stepper-item-name"
              [attr.aria-label]="
                isStepDisabled(counter)
                  ? undefined
                  : ('GlobalApplicativeConstants.accessibility.labels.stepperBtnLabel'
                    | translate
                      : {
                          step: counter + 1,
                          stepName: (step.titleShort | translate),
                          isActive: isStepActive(counter)
                        })
              "
            >
              {{ step.titleShort | translate }}
            </div>
          </li>
        </ol>
      </div>
      <div class="sc-design-caption mobile-active-item-name">
        {{ getCurrentStepTitle() | translate }}
      </div>
      <div class="spacer"></div>
    </nav>
  `,
  imports: [NgFor, NgIf, IconComponent, NgClass, AsyncPipe, TranslateModule],
})
export class StepperComponent {
  @Input() steps: StepperStep[] = [];
  @Input() selectedIndex: number;
  @Input() disabled = false;
  @Input() selectedIsCurrentPage = false;
  @Input() selectedIsCompleted = false;

  fixedBreadcrumbItems: Observable<string[]>;

  constructor(private readonly translator: TranslatorService) {
    this.fixedBreadcrumbItems = this.translator.getCurrentLang().pipe(
      map(() => {
        const resultTranslations: string[] = [];

        let i = 0;
        let currentTranslation: string;
        let translationExists = true;
        while (!!translationExists && i < 10) {
          const translationKey = `Stepper.fixedBreadcrumbs.item_${i}`;

          currentTranslation =
            this.translator.getInstantTranslation(translationKey);
          translationExists =
            !!currentTranslation && currentTranslation !== translationKey;

          if (translationExists) {
            resultTranslations.push(currentTranslation);
          }
          i++;
        }

        return resultTranslations;
      }),
      share()
    );
  }

  getCurrentStepTitle() {
    return (
      this.steps[this.selectedIndex - 1]?.titleShort ??
      this.steps[this.steps.length - 1].titleShort
    );
  }

  isCurrentStep(stepIndex: number) {
    return stepIndex === this.selectedIndex;
  }

  isStepComplete(stepIndex: number) {
    return (
      stepIndex + 1 < this.selectedIndex ||
      (stepIndex + 1 === this.selectedIndex && this.selectedIsCompleted)
    );
  }

  isStepActive(stepIndex: number) {
    return stepIndex + 1 === this.selectedIndex;
  }

  isStepDisabled(stepIndex: number) {
    return (
      this.disabled ||
      this.steps[stepIndex]?.disabled ||
      stepIndex + 1 > this.selectedIndex ||
      (stepIndex === this.selectedIndex && this.selectedIsCurrentPage)
    );
  }

  getAriaCurrent(stepIndex: number) {
    if (this.selectedIndex === stepIndex + 1) {
      if (this.selectedIsCurrentPage) {
        return 'page';
      }

      return 'true';
    }

    return undefined;
  }

  callStepAction(event: Event, step: StepperStep, stepIndex: number) {
    if (!this.isStepDisabled(stepIndex)) {
      step.action(event);
    }
  }
}

export interface StepperStep {
  titleShort: string;
  titleLong?: string;
  disabled?: boolean;
  action: Function;
}
